import { Api } from 'app/shared/lib/api/api';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { deepClone } from 'app/shared/lib/utilities';
import { autoinject } from 'aurelia-framework';

const USER_STORED_OBJECT_KEY = 'app-user';
const USER_GRID_STORAGE_PATH = 'ui/grids/settings.json';
const ADMIN_STORED_OBJECT_KEY = 'app-admin';
const ADMIN_GRID_STORAGE_PATH = 'ui/grids/admin.json';

export interface ICellConfig {
    key: string
    top: number;
    left: number;
    height: number;
    width: number;
}
export type GridConfig = ICellConfig[];
type GridConfigMap = Record<string, GridConfig>;

@autoinject()
export class GridConfigurationService {

    protected readonly config: GridConfigMap = {};
    protected readonly templateConfig: GridConfigMap = {};
    protected loaded: Promise<void>;

    constructor(protected readonly api: Api, protected readonly logger: AppLogger) {
        this.loaded = this.load();
    }

    public async getGridConfiguration(grid: string) {
        return this.getConfiguration(grid);
    }

    public async getGridTemplateConfiguration(grid: string) {
        return this.getConfiguration(grid, this.templateConfig);
    }

    protected async getConfiguration(grid: string, config: GridConfigMap = this.config): Promise<GridConfig | undefined> {
        try {
            await this.loaded;
        }
        catch(err) {
            this.logger.warn(`Could not load configuration for grid ${grid}.`, err);
        }
        finally {
            return deepClone(config[grid]);
        }
    }

    public async updateGridConfiguration(grid: string, config: GridConfig) {
        if(!grid) {
            return;
        }
        this.config[grid] = config;
        await this.api.put(this.api.storedObject(USER_STORED_OBJECT_KEY, USER_GRID_STORAGE_PATH, this.config));
    }

    public async updateGridTemplateConfiguration(grid: string, config: GridConfig) {
        if(!grid) {
            return this.logger.warn('Tried to save a grid without a key. This grid will not save until the grid is given a key property.');
        }
        this.templateConfig[grid] = config;
        await this.api.put(this.api.storedObject(ADMIN_STORED_OBJECT_KEY, ADMIN_GRID_STORAGE_PATH, this.templateConfig));
    }

    protected async load() {
        const [templateConfig, config] = await Promise.all<Record<string, GridConfig>, Record<string, GridConfig>>([
            this.api.get(this.api.storedObject(ADMIN_STORED_OBJECT_KEY, ADMIN_GRID_STORAGE_PATH))
                .catch(err => {
                    if(err.httpStatusCode === 404) {
                        return {};
                    }
                    throw err;
                }),
            this.api.get(this.api.storedObject(USER_STORED_OBJECT_KEY, USER_GRID_STORAGE_PATH))
                .catch(err => {
                    if(err.httpStatusCode === 404) {
                        return {};
                    }
                    throw err;
                })
        ]);
        Object.assign(this.templateConfig, templateConfig);
        Object.assign(this.config, config);
    }
}
